import React from "react";
import { useTranslation } from "react-i18next";
import {
  GoogleMap,
  OverlayView,
  Polyline,
  useJsApiLoader,
} from "@react-google-maps/api";
import polyline from "@mapbox/polyline";
import { driverHeading } from "../driverRoute";
import { mapStyles } from "../../web-receipt/MapStyles";

// MapView for the hailing flow — real Google Maps (codebase standard, same
// API key + styles as web-receipt/MapView). Shows the pickup marker always
// and the drop-off marker once a destination exists.

export interface MapViewProps {
  start?: { lat: number; lng: number } | undefined;
  end?: { lat: number; lng: number } | undefined;
  stop?: { lat: number; lng: number } | undefined;
  encodedPolyline?: string | undefined;
  driverPosition?: { lat: number; lng: number } | undefined;
  /** The driver's operating area; picks the taxi colour (URBAN red, NT green, LANTAU blue). */
  driverOperatingArea?: string | undefined;
  zoom?: number;
  apiKey?: string;
}

const startMarkerStyle: React.CSSProperties = {
  position: "absolute",
  left: "-20px",
  top: "-48px",
  width: "40px",
  height: "48px",
  pointerEvents: "none",
};

// Marker and route styling follows the web receipt's map (web-receipt/MapView).
const endMarkerStyle: React.CSSProperties = {
  position: "absolute",
  left: "-18px",
  top: "-36px",
  width: "36px",
  height: "36px",
  pointerEvents: "none",
};

const stopMarkerStyle: React.CSSProperties = {
  display: "flex",
  width: "28px",
  height: "28px",
  alignItems: "center",
  justifyContent: "center",
  border: "3px solid white",
  borderRadius: "50%",
  background: "#1479C7",
  boxShadow: "0 2px 6px rgba(15, 21, 26, 0.35)",
  color: "white",
  fontFamily: "sans-serif",
  fontSize: "13px",
  fontWeight: 700,
  transform: "translate(-50%, -50%)",
  pointerEvents: "none",
};

// Top-down taxis that can turn to any heading: the web receipt's red (urban) taxi and
// recoloured copies of it for the NT (green) and Lantau (blue) taxis.
const taxiIconFor = (operatingArea: string | undefined): string => {
  switch (operatingArea?.toUpperCase()) {
    case "NT":
      return "/taxi_icon2_nt.png";
    case "LANTAU":
      return "/taxi_icon2_lantau.png";
    default:
      return "/taxi_icon2.png";
  }
};

// Fallback center (Central, HK) while no pickup is picked yet — the map
// always needs a center even before the first search resolves.
const FALLBACK_CENTER = { lat: 22.2819, lng: 114.1589 };

const MapView: React.FC<MapViewProps> = ({
  start,
  end,
  stop,
  encodedPolyline,
  driverPosition,
  driverOperatingArea,
  zoom = 15,
  apiKey = process.env.REACT_APP_GOOGLE_MAP_API_KEY || "",
}) => {
  const { t } = useTranslation("hailing");
  const { isLoaded, loadError } = useJsApiLoader({
    googleMapsApiKey: apiKey,
  });
  const [map, setMap] = React.useState<google.maps.Map | null>(null);
  const hasFittedDriverRef = React.useRef(false);
  const routePath = React.useMemo(() => {
    if (!encodedPolyline) return [];
    try {
      return polyline
        .decode(encodedPolyline)
        .map(([lat, lng]) => ({ lat, lng }));
    } catch (error) {
      console.error("Could not decode the hailing route polyline.", error);
      return [];
    }
  }, [encodedPolyline]);
  // As on the web receipt, the taxi turns to face along its route.
  const taxiHeading = React.useMemo(
    () => (driverPosition ? driverHeading(routePath, driverPosition) : 0),
    [driverPosition, routePath],
  );

  React.useEffect(() => {
    if (!map) return;

    const points = [start, stop, end, ...routePath].filter(
      (point): point is { lat: number; lng: number } => point != null,
    );
    if (points.length === 0) {
      map.panTo(FALLBACK_CENTER);
      map.setZoom(zoom);
      return;
    }
    if (points.length === 1) {
      map.panTo(points[0]!);
      map.setZoom(zoom);
      return;
    }

    const bounds = new window.google.maps.LatLngBounds();
    points.forEach((point) => bounds.extend(point));
    map.fitBounds(bounds, 72);
  }, [map, start, stop, end, routePath, zoom]);

  React.useEffect(() => {
    if (!map || !driverPosition || hasFittedDriverRef.current) return;

    const points = [start, stop, end, driverPosition, ...routePath].filter(
      (point): point is { lat: number; lng: number } => point != null,
    );
    if (points.length === 1) {
      map.panTo(points[0]!);
      map.setZoom(zoom);
    } else {
      const bounds = new window.google.maps.LatLngBounds();
      points.forEach((point) => bounds.extend(point));
      map.fitBounds(bounds, 72);
    }
    hasFittedDriverRef.current = true;
  }, [map, start, stop, end, driverPosition, routePath, zoom]);

  if (loadError) {
    return (
      <div className="flex h-full w-full items-center justify-center bg-[#E8ECEF] text-[14px] text-[#6B7075]">
        Map unavailable
      </div>
    );
  }

  if (!isLoaded) {
    return (
      <div className="flex h-full w-full items-center justify-center bg-[#E8ECEF] text-[14px] text-[#6B7075]">
        Loading map…
      </div>
    );
  }

  return (
    <div className="h-full w-full overflow-hidden">
      <GoogleMap
        mapContainerClassName="h-full w-full"
        center={start ?? FALLBACK_CENTER}
        zoom={zoom}
        onLoad={setMap}
        onUnmount={() => setMap(null)}
        options={{
          mapTypeControl: false,
          streetViewControl: false,
          fullscreenControl: false,
          clickableIcons: false,
          styles: mapStyles,
        }}
      >
        {routePath.length > 1 && (
          <Polyline
            path={routePath}
            options={{
              strokeColor: "#2563eb",
              strokeWeight: 4,
              clickable: false,
            }}
          />
        )}
        {start && (
          <OverlayView
            position={start}
            mapPaneName={OverlayView.OVERLAY_MOUSE_TARGET}
          >
            <div style={startMarkerStyle}>
              <img
                src="/map-pickup-icon.png"
                alt={t("map.pickup")}
                width={40}
                height={45}
                style={{ display: "block", width: "40px", height: "45px", pointerEvents: "none" }}
              />
            </div>
          </OverlayView>
        )}
        {end && (
          <OverlayView
            position={end}
            mapPaneName={OverlayView.OVERLAY_MOUSE_TARGET}
          >
            <div style={endMarkerStyle}>
              <img
                src="/map-dropoff-icon.png"
                alt={t("map.dropoff")}
                width={40}
                height={45}
                style={{ display: "block", width: "40px", height: "45px", pointerEvents: "none" }}
              />
            </div>
          </OverlayView>
        )}
        {stop && (
          <OverlayView
            position={stop}
            mapPaneName={OverlayView.OVERLAY_MOUSE_TARGET}
          >
            <div style={stopMarkerStyle} aria-label={t("map.stop")}>
              3
            </div>
          </OverlayView>
        )}
        {driverPosition && (
          <OverlayView
            position={driverPosition}
            mapPaneName={OverlayView.OVERLAY_MOUSE_TARGET}
            zIndex={1000}
          >
            <div
              role="img"
              aria-label={t("map.driverLocation")}
              style={{
                position: "absolute",
                left: 0,
                top: 0,
                display: "flex",
                width: "36px",
                height: "36px",
                alignItems: "center",
                justifyContent: "center",
                transform: "translate(-50%, -50%)",
                pointerEvents: "none",
              }}
            >
              <img
                src={taxiIconFor(driverOperatingArea)}
                alt=""
                width={30}
                height={30}
                data-heading={Math.round(taxiHeading)}
                // Sized in CSS as on the web receipt: the image is 15x31, and Tailwind's
                // `img { height: auto }` would otherwise scale it to 30x62.
                style={{
                  display: "block",
                  width: "30px",
                  height: "30px",
                  objectFit: "contain",
                  transform: `rotate(${taxiHeading}deg)`,
                  transformOrigin: "center",
                  transition: "transform 0.2s linear",
                }}
              />
            </div>
          </OverlayView>
        )}
      </GoogleMap>
    </div>
  );
};

export default MapView;
